import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bot, MessageSquare, Headphones, Sparkles, HelpCircle, Phone, Heart, Star, Zap, Upload, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/services/api';

const WIDGET_STYLES = [
  { val: 'floating', label: 'Floating Button', desc: 'Circle in corner' },
  { val: 'side_tab', label: 'Side Tab', desc: 'Vertical tab on edge' },
];

const BUTTON_ICONS = [
  { id: 'chat',       label: 'Chat',     icon: <MessageSquare className="w-5 h-5" /> },
  { id: 'bot',        label: 'Bot',      icon: <Bot className="w-5 h-5" /> },
  { id: 'headphones', label: 'Support',  icon: <Headphones className="w-5 h-5" /> },
  { id: 'sparkles',   label: 'AI',       icon: <Sparkles className="w-5 h-5" /> },
  { id: 'help',       label: 'Help',     icon: <HelpCircle className="w-5 h-5" /> },
  { id: 'phone',      label: 'Phone',    icon: <Phone className="w-5 h-5" /> },
  { id: 'heart',      label: 'Friendly', icon: <Heart className="w-5 h-5" /> },
  { id: 'star',       label: 'Premium',  icon: <Star className="w-5 h-5" /> },
  { id: 'zap',        label: 'Fast',     icon: <Zap className="w-5 h-5" /> },
];

const ButtonIconPreview = ({ iconId }) => {
  const map = {
    bot: <Bot className="w-5 h-5 text-white" />,
    headphones: <Headphones className="w-5 h-5 text-white" />,
    sparkles: <Sparkles className="w-5 h-5 text-white" />,
    help: <HelpCircle className="w-5 h-5 text-white" />,
    phone: <Phone className="w-5 h-5 text-white" />,
    heart: <Heart className="w-5 h-5 text-white" />,
    star: <Star className="w-5 h-5 text-white" />,
    zap: <Zap className="w-5 h-5 text-white" />,
  };
  return map[iconId] || <MessageSquare className="w-5 h-5 text-white" />;
};

const COLOR_FIELDS = [
  // Labels match the CreateAgentWizard preview + rendered chat widget so the
  // same swatch controls the same on-screen element across all three surfaces
  // (Wizard preview, Details page preview, live chat UI).
  { key: 'primary_color',    label: 'Header / Brand Color', hint: 'Widget header, launcher button, and outgoing bubble background.' },
  { key: 'secondary_color',  label: 'Accent Color',         hint: 'Used for subtle gradients and secondary UI accents.' },
  { key: 'text_color',       label: 'Message Text Color',   hint: 'Colour of the assistant\u2019s reply text.' },
  { key: 'background_color', label: 'Chat Background',      hint: 'Background behind incoming messages and the input row.' },
];

/**
 * Theme tab for ChatbotDetailPage — widget colour, style, position, icon, label,
 * plus a live preview pane.
 */
export const ThemeTab = ({ theme, setTheme, formData, chatbotId }) => {
  const isLeft = (theme.position || '').includes('left');
  const fileInputRef = useRef(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const handleIconUpload = async (file) => {
    if (!file) return;
    if (!chatbotId) { toast.error('Save the AI Employee before uploading an icon'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file (PNG, SVG, JPG, WEBP)'); return; }
    if (file.size > 1024 * 1024) { toast.error('Icon must be under 1MB'); return; }
    setUploadingIcon(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/chatbots/${chatbotId}/widget-icon`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res?.data?.widget_icon_url;
      if (url) {
        setTheme({ ...theme, widget_icon_url: url });
        toast.success('Icon uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to upload icon');
    } finally {
      setUploadingIcon(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeUploadedIcon = () => {
    setTheme({ ...theme, widget_icon_url: '' });
  };

  // Resolve full URL for icon preview (icon URL is stored as relative /api/... path).
  const iconPreviewSrc = (() => {
    const u = theme.widget_icon_url || '';
    if (!u) return '';
    if (u.startsWith('http')) return u;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base.replace(/\/$/, '')}${u}`;
  })();

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-slate-900">Widget Theme</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {COLOR_FIELDS.map(({ key, label, hint }) => (
              <div key={key}>
                <Label className="text-slate-600">{label}</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    value={theme[key]}
                    onChange={(e) => setTheme({ ...theme, [key]: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                    data-testid={`theme-${key}-picker`}
                  />
                  <Input
                    value={theme[key]}
                    onChange={(e) => setTheme({ ...theme, [key]: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 flex-1"
                    data-testid={`theme-${key}-input`}
                  />
                </div>
                {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
              </div>
            ))}
          </div>

          <div>
            <Label className="text-slate-600">Border Radius (px)</Label>
            <Input
              type="number"
              value={theme.border_radius}
              onChange={(e) => setTheme({ ...theme, border_radius: parseInt(e.target.value) || 0 })}
              className="bg-white border-slate-300 text-slate-900 mt-1 w-32"
            />
          </div>

          {/* Widget Style */}
          <div>
            <Label className="text-slate-600 mb-2 block">Widget Style</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              {WIDGET_STYLES.map(s => (
                <button
                  key={s.val}
                  type="button"
                  onClick={() => setTheme({ ...theme, widget_style: s.val })}
                  className={cn(
                    'text-left p-3 rounded-lg border-2 transition-all',
                    theme.widget_style === s.val ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <p className="text-sm font-medium text-slate-900">{s.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Position */}
          <div>
            <Label className="text-slate-600">
              {theme.widget_style === 'side_tab' ? 'Tab Side' : 'Widget Position'}
            </Label>
            <Select value={theme.position} onValueChange={(v) => setTheme({ ...theme, position: v })}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 w-48 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {theme.widget_style === 'side_tab' ? (
                  <>
                    <SelectItem value="right" className="text-slate-700">Right Edge</SelectItem>
                    <SelectItem value="left" className="text-slate-700">Left Edge</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="bottom-right" className="text-slate-700">Bottom Right</SelectItem>
                    <SelectItem value="bottom-left" className="text-slate-700">Bottom Left</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Button Icon Selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-600 block">Chat Button Icon</Label>
              {iconPreviewSrc && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={removeUploadedIcon}
                  className="h-7 text-xs text-red-600 hover:bg-red-50"
                  data-testid="remove-custom-icon-btn"
                >
                  <X className="w-3 h-3 mr-1" /> Remove custom icon
                </Button>
              )}
            </div>

            {iconPreviewSrc ? (
              <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-teal-500 bg-teal-50">
                <div className="w-12 h-12 rounded-lg bg-white border border-teal-200 flex items-center justify-center overflow-hidden">
                  <img src={iconPreviewSrc} alt="Custom icon" className="max-w-full max-h-full object-contain" data-testid="custom-icon-preview" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-teal-900">Custom icon in use</p>
                  <p className="text-xs text-teal-700 truncate">Shown in the chat button instead of the default icons below.</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingIcon}
                  className="border-teal-300 text-teal-700 hover:bg-teal-100"
                  data-testid="replace-custom-icon-btn"
                >
                  {uploadingIcon ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Replace'}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {BUTTON_ICONS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setTheme({ ...theme, widget_button_icon: item.id })}
                      data-testid={`icon-btn-${item.id}`}
                      className={cn(
                        'flex flex-col items-center gap-1 p-2.5 rounded-lg border-2 transition-all',
                        theme.widget_button_icon === item.id
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      )}
                    >
                      {item.icon}
                      <span className="text-xs">{item.label}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingIcon}
                    data-testid="upload-custom-icon-btn"
                    className="flex flex-col items-center gap-1 p-2.5 rounded-lg border-2 border-dashed border-slate-300 hover:border-teal-400 hover:bg-teal-50 text-slate-500 hover:text-teal-700 transition-all"
                  >
                    {uploadingIcon ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                    <span className="text-xs">Upload</span>
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-2">Upload your own PNG, SVG or WEBP (max 1MB). The icon auto-sizes to match the button label.</p>
              </>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
              className="hidden"
              onChange={(e) => handleIconUpload(e.target.files?.[0])}
              data-testid="custom-icon-file-input"
            />
          </div>

          {/* Button Text Label */}
          <div>
            <Label className="text-slate-600 mb-1 block">Button Label <span className="text-slate-400 text-xs">(optional, max 20 chars)</span></Label>
            <Input
              value={theme.widget_button_text || ''}
              onChange={(e) => setTheme({ ...theme, widget_button_text: e.target.value.slice(0, 20) })}
              placeholder="e.g. Chat with us"
              className="bg-white border-slate-300 text-slate-900 mt-1"
              data-testid="widget-button-text-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Widget Preview */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-slate-900">Live Preview</CardTitle></CardHeader>
        <CardContent>
          <div className="relative bg-slate-100 rounded-xl overflow-hidden" style={{ height: 320 }}>
            {/* FLOATING style preview */}
            {theme.widget_style !== 'side_tab' && (
              <div className={`absolute bottom-4 ${isLeft ? 'left-4' : 'right-4'} flex flex-col gap-2 items-end`}>
                <div className="w-56 rounded-xl overflow-hidden shadow-lg border border-slate-200" style={{ backgroundColor: theme.background_color, borderRadius: theme.border_radius }}>
                  <div className="p-3" style={{ background: theme.primary_color }}>
                    <p className="text-white text-xs font-semibold">{formData.name || 'AI Employee'}</p>
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="text-xs p-2 rounded-lg bg-slate-100" style={{ color: theme.text_color }}>
                      {formData.welcome_message?.slice(0, 50) || 'Hello! How can I help?'}
                    </div>
                    <div className="text-xs p-2 rounded-lg text-white ml-auto w-fit" style={{ backgroundColor: theme.primary_color }}>Hi there!</div>
                  </div>
                </div>
                <div
                  className={`flex items-center gap-2 h-12 shadow-lg cursor-pointer ${theme.widget_button_text ? 'rounded-full px-4 py-2' : 'w-12 rounded-full justify-center'}`}
                  style={{ background: theme.primary_color }}
                >
                  {iconPreviewSrc ? (
                    <img src={iconPreviewSrc} alt="" className="w-5 h-5 object-contain" />
                  ) : (
                    <ButtonIconPreview iconId={theme.widget_button_icon} />
                  )}
                  {theme.widget_button_text && <span className="text-white text-xs font-bold">{theme.widget_button_text}</span>}
                </div>
              </div>
            )}

            {/* SIDE TAB style preview */}
            {theme.widget_style === 'side_tab' && (
              <div className={`absolute top-1/2 -translate-y-1/2 ${isLeft ? 'left-0' : 'right-0'} flex`}>
                <div
                  className={`flex flex-col items-center gap-2 py-4 px-2.5 cursor-pointer shadow-lg ${isLeft ? 'rounded-r-xl' : 'rounded-l-xl'}`}
                  style={{ background: theme.primary_color }}
                >
                  {iconPreviewSrc ? (
                    <img src={iconPreviewSrc} alt="" className="w-5 h-5 object-contain" />
                  ) : (
                    <ButtonIconPreview iconId={theme.widget_button_icon} />
                  )}
                  <span
                    className="text-white text-xs font-bold"
                    style={{
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                      transform: isLeft ? 'rotate(180deg)' : 'rotate(0deg)',
                      letterSpacing: '1px'
                    }}
                  >
                    {theme.widget_button_text || 'Chat'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Embed code preview update notice */}
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
        Save your changes — the embed code in the <strong>Deploy</strong> tab will update automatically to include your icon and style settings.
      </div>
    </div>
  );
};

export default ThemeTab;
